import { useEffect, useRef, useMemo } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { useOnboardingTour } from "./use-onboarding-tour"
import { buildTourSteps } from "./tour-steps"
import { OnboardingWelcome } from "./onboarding-welcome"
import { OnboardingSpotlight } from "./onboarding-spotlight"
import { OnboardingTooltip } from "./onboarding-tooltip"
import type { IParcel } from "@/types/explore"

interface OnboardingTourProps {
  setLayersOpen: (open: boolean) => void
  setSelectedParcels: (parcels: IParcel[]) => void
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  selectedParcels: IParcel[]
}

export const OnboardingTour = ({
  setLayersOpen,
  setSelectedParcels,
  setParcelPOI,
  selectedParcels,
}: OnboardingTourProps) => {
  const { active, currentStep, next, prev, skip, markComplete } = useOnboardingTour()
  const prevStepRef = useRef(-1)
  const selectedParcelsRef = useRef(selectedParcels)
  selectedParcelsRef.current = selectedParcels

  const steps = useMemo(
    () => buildTourSteps({ setLayersOpen, setSelectedParcels, setParcelPOI, selectedParcelsRef }),
    [setLayersOpen, setSelectedParcels, setParcelPOI],
  )

  // Call onEnter/onExit when step changes
  useEffect(() => {
    if (!active) return
    const prevStep = prevStepRef.current

    if (prevStep >= 0 && prevStep < steps.length) {
      steps[prevStep].onExit?.()
    }
    if (currentStep >= 0 && currentStep < steps.length) {
      steps[currentStep].onEnter?.()
    }
    prevStepRef.current = currentStep
  }, [active, currentStep, steps])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const step = prevStepRef.current
      if (step >= 0 && step < steps.length) {
        steps[step].onExit?.()
      }
    }
  }, [steps])

  if (!active) return null

  const isWelcome = currentStep === -1
  const step = currentStep >= 0 && currentStep < steps.length ? steps[currentStep] : null
  const isComplete = currentStep >= steps.length

  // Completion screen
  if (isComplete) {
    markComplete()
    return (
      <div className="absolute inset-0 z-[45] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/30" onClick={markComplete} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-6 max-w-xs text-center"
        >
          <h2 className="text-lg font-bold text-gray-800 mb-2">Bonne exploration !</h2>
          <p className="text-sm text-gray-500 mb-4">
            Appuyez sur{" "}
            <kbd className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs font-mono rounded border border-gray-200">
              ?
            </kbd>{" "}
            à tout moment pour revoir les raccourcis.
          </p>
          <button
            onClick={markComplete}
            className="px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: "#1E40AF" }}
          >
            Commencer
          </button>
        </motion.div>
      </div>
    )
  }

  const handleNext = () => {
    next()
  }

  return (
    <>
      <AnimatePresence mode="wait">{isWelcome && <OnboardingWelcome onStart={next} onSkip={skip} />}</AnimatePresence>

      {step && (
        <>
          <OnboardingSpotlight targetSelector={step.target} active={true} onClick={handleNext} />
          <OnboardingTooltip
            targetSelector={step.target}
            title={step.title}
            body={step.body}
            stepIndex={currentStep}
            totalSteps={steps.length}
            onNext={handleNext}
            onPrev={prev}
            onSkip={skip}
            active={true}
          />
        </>
      )}
    </>
  )
}
